import { Ban, AlertTriangle, CheckCircle2 } from "lucide-react";
import type { SensitivityKey } from "../utils/platformSensitivity";

const POLICY_URL =
  "https://brookfieldrp.sharepoint.com/teams/AISupportBot/Shared%20Documents/Public/AI%20Policies/AI%20Policy.pdf";

// Mirrors the color/icon/key mapping in utils/platformSensitivity.ts so the
// legend, the card badges, and the click-to-filter behavior all agree.
const ITEMS: {
  key: SensitivityKey;
  icon: typeof Ban;
  cls: string;
  title: string;
  body: string;
}[] = [
  {
    key: "private",
    icon: CheckCircle2,
    cls: "card-sensitivity-private",
    title: "Approved for High Disclosure Risk Data",
    body: "The platform can be used to process high risk information, including financial, employee and client records.",
  },
  {
    key: "public",
    icon: AlertTriangle,
    cls: "card-sensitivity-public",
    title: "Approved for Low Disclosure Risk Data",
    body: "The platform can only be used to process low risk data (data that can be made public with no risk to our organization).",
  },
  {
    key: "pending",
    icon: Ban,
    cls: "card-sensitivity-pending",
    title: "Not Approved",
    body: "The platform is under review and cannot be used for high or low risk data at this time.",
  },
];

type Props = {
  selected: SensitivityKey | null;
  onToggle: (key: SensitivityKey) => void;
};

export function PlatformSensitivityLegend({ selected, onToggle }: Props) {
  return (
    <div className="sensitivity-legend">
      <div className="sensitivity-legend-items">
        {ITEMS.map((item) => {
          const active = selected === item.key;
          return (
            <button
              type="button"
              key={item.key}
              className={`sensitivity-legend-item${active ? " is-active" : ""}`}
              onClick={() => onToggle(item.key)}
              aria-pressed={active}
              title={`Filter to "${item.title}"`}
            >
              <span className={`sensitivity-legend-icon ${item.cls}`} aria-hidden>
                <item.icon size={18} />
              </span>
              <span className="sensitivity-legend-text">
                <span className="sensitivity-legend-title">{item.title}</span>
                <span className="sensitivity-legend-body">{item.body}</span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="sensitivity-legend-footer">
        Always consult the{" "}
        <a href={POLICY_URL} target="_blank" rel="noopener noreferrer">
          AI Policy
        </a>{" "}
        before using a new AI Platform.
      </p>
    </div>
  );
}
